import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  BarChart3, ArrowLeft, Loader2, TrendingUp, Calculator,
  FileText, Download, DollarSign, Receipt
} from 'lucide-react';

export default function FiscalRelatorios() {
  const [loading, setLoading] = useState(true);
  const [resumo, setResumo] = useState(null);
  const [relatorioISS, setRelatorioISS] = useState(null);
  const [filtros, setFiltros] = useState({
    competencia_mes: new Date().getMonth() + 1,
    competencia_ano: new Date().getFullYear()
  });
  const [carregandoISS, setCarregandoISS] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    loadResumo();
  }, []);

  const loadResumo = async () => {
    setLoading(true);
    try {
      const response = await api.get('/fiscal/relatorios/resumo', {
        params: { competencia_ano: filtros.competencia_ano }
      });
      setResumo(response.data);
    } catch (error) {
      toast.error('Erro ao carregar resumo fiscal');
    } finally {
      setLoading(false);
    }
  };

  const loadRelatorioISS = async () => {
    setCarregandoISS(true);
    try {
      const response = await api.get('/fiscal/relatorios/iss', {
        params: {
          competencia_mes: filtros.competencia_mes,
          competencia_ano: filtros.competencia_ano
        }
      });
      setRelatorioISS(response.data);
    } catch (error) {
      toast.error('Erro ao carregar relatório de ISS');
    } finally {
      setCarregandoISS(false);
    }
  };

  const exportarCSV = () => {
    if (!relatorioISS?.notas?.length) {
      toast.error('Nenhum dado para exportar');
      return;
    }

    const headers = ['Número', 'Tomador', 'Valor', 'ISS'];
    const rows = relatorioISS.notas.map(n => [
      n.numero || '',
      n.tomador || '',
      n.valor?.toFixed(2) || '0.00',
      n.iss?.toFixed(2) || '0.00'
    ]);

    const csv = [
      `Relatório ISS - ${relatorioISS.competencia}`,
      '',
      headers.join(';'),
      ...rows.map(r => r.join(';')),
      '',
      `Total Base de Cálculo;${relatorioISS.base_calculo?.toFixed(2)}`,
      `Total ISS;${relatorioISS.valor_iss?.toFixed(2)}`
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relatorio_iss_${filtros.competencia_mes}_${filtros.competencia_ano}.csv`;
    link.click();
  };

  const meses = [
    { value: 1, label: 'Janeiro' },
    { value: 2, label: 'Fevereiro' },
    { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Maio' },
    { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' },
    { value: 11, label: 'Novembro' },
    { value: 12, label: 'Dezembro' }
  ];

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-[400px]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="fiscal-relatorios">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/fiscal')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6" />
            Relatórios Fiscais
          </h1>
          <p className="text-muted-foreground">Faturamento, ISS e análises fiscais</p>
        </div>
      </div>

      {/* Cards de resumo anual */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Notas Emitidas</CardTitle>
            <FileText className="w-5 h-5 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{resumo?.resumo?.total_notas || 0}</div>
            <p className="text-xs text-muted-foreground">Em {filtros.competencia_ano}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Faturamento Total</CardTitle>
            <DollarSign className="w-5 h-5 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              R$ {(resumo?.resumo?.total_faturado || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
            <p className="text-xs text-muted-foreground">Em {filtros.competencia_ano}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">ISS Total</CardTitle>
            <Calculator className="w-5 h-5 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              R$ {(resumo?.resumo?.total_iss || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
            <p className="text-xs text-muted-foreground">Em {filtros.competencia_ano}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Ticket Médio</CardTitle>
            <TrendingUp className="w-5 h-5 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              R$ {(resumo?.resumo?.ticket_medio || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
            <p className="text-xs text-muted-foreground">Por nota fiscal</p>
          </CardContent>
        </Card>
      </div>

      {/* Faturamento por mês */}
      {resumo?.faturamento_por_mes?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Faturamento Mensal - {filtros.competencia_ano}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Mês</TableHead>
                    <TableHead className="text-right">Quantidade</TableHead>
                    <TableHead className="text-right">Faturamento</TableHead>
                    <TableHead className="text-right">ISS</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {resumo.faturamento_por_mes.map((mes, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{mes.mes}</TableCell>
                      <TableCell className="text-right">{mes.quantidade}</TableCell>
                      <TableCell className="text-right">
                        R$ {mes.faturado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right text-purple-600">
                        R$ {mes.iss.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Relatório de ISS por competência */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5" />
            Relatório de ISS
          </CardTitle>
          <CardDescription>
            Gere o relatório de ISS para declaração mensal
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <Label>Competência</Label>
              <div className="flex gap-2">
                <Select 
                  value={String(filtros.competencia_mes)} 
                  onValueChange={(v) => setFiltros(prev => ({ ...prev, competencia_mes: parseInt(v) }))}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {meses.map(m => (
                      <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select 
                  value={String(filtros.competencia_ano)} 
                  onValueChange={(v) => setFiltros(prev => ({ ...prev, competencia_ano: parseInt(v) }))}
                >
                  <SelectTrigger className="w-[100px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026].map(ano => (
                      <SelectItem key={ano} value={String(ano)}>{ano}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button onClick={loadRelatorioISS} disabled={carregandoISS}>
              {carregandoISS ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Carregando...
                </>
              ) : (
                <>
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Gerar Relatório
                </>
              )}
            </Button>

            {relatorioISS && (
              <Button variant="outline" onClick={exportarCSV}>
                <Download className="w-4 h-4 mr-2" />
                Exportar CSV
              </Button>
            )}
          </div>

          {/* Resultado do relatório */}
          {relatorioISS && (
            <div className="mt-6 space-y-4">
              <div className="p-4 bg-purple-50 rounded-lg">
                <h4 className="font-medium text-purple-900 mb-2">
                  Resumo - Competência {relatorioISS.competencia}
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-purple-600">Quantidade de Notas</p>
                    <p className="text-xl font-bold">{relatorioISS.quantidade_notas}</p>
                  </div>
                  <div>
                    <p className="text-sm text-purple-600">Base de Cálculo</p>
                    <p className="text-xl font-bold">
                      R$ {relatorioISS.base_calculo.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-purple-600">Alíquota Média</p>
                    <p className="text-xl font-bold">{relatorioISS.aliquota_media}%</p>
                  </div>
                  <div>
                    <p className="text-sm text-purple-600">Valor ISS a Recolher</p>
                    <p className="text-xl font-bold text-purple-700">
                      R$ {relatorioISS.valor_iss.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              </div>

              {relatorioISS.notas?.length > 0 && (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Número</TableHead>
                        <TableHead>Tomador</TableHead>
                        <TableHead className="text-right">Valor</TableHead>
                        <TableHead className="text-right">ISS</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {relatorioISS.notas.map((nota, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-mono">{nota.numero || '-'}</TableCell>
                          <TableCell>{nota.tomador}</TableCell>
                          <TableCell className="text-right">
                            R$ {(nota.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell className="text-right text-purple-600">
                            R$ {(nota.iss || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
